"use client"

import { useCallback, useEffect, useRef } from "react"
import { useRouter } from "next/router"

import {
  COVI_HIGHLIGHT_EVENT,
  type CoviHighlightDetail,
} from "@/lib/coviEvents"
import { buildWorkspaceAwareNavigation } from "@/utils/workspaceRoutes"

import {
  applyTransientHighlight,
  COVI_HIGHLIGHT_DURATION_MS,
  COVI_HIGHLIGHT_PARAM,
  readHighlightId,
} from "../utils/highlight"

/**
 * Glow-then-fade the element Sofia just created or changed.
 *
 * Mount this once at the app-shell level (it's in CoviMount, so it survives
 * client-side navigation and is always listening). It drives the glow from TWO
 * triggers so the highlight fires whether or not a navigation happened:
 *
 * 1. In-app signal (`COVI_HIGHLIGHT_EVENT`): the common case — Sofia creates or
 *    edits a resource while you're already on its page and never navigates. The
 *    panel dispatches the event with the target id the moment the write lands;
 *    this hook glows the matching element on the CURRENT page.
 * 2. URL param (`?covi_highlight=<id>`): Sofia creates/edits and then navigates
 *    you to the resource. The receipt deep link carries the id; this hook reads
 *    it on arrival, glows the element, and strips the param so a reload or
 *    back-navigation never re-glows.
 *
 * Both find the element by `#<id>` or `[data-covi-highlight-id="<id>"]`, apply
 * the transient `covi-highlight` glow, and auto-clear it after a few seconds.
 * The target often mounts AFTER the trigger fires (the page is still fetching
 * the freshly-created resource, or a navigation is in flight), so the glow
 * polls briefly with requestAnimationFrame until the element appears or a short
 * budget elapses, rather than assuming it's already in the DOM.
 *
 * Reduced motion is honored two ways: the glow keyframes fall back to a brief
 * static ring via the reduced-motion media query in `styles/globals.css`, and
 * the JS-driven `scrollIntoView` drops from `smooth` to `auto` (see
 * `prefersReducedMotion` below), since that animation isn't governed by CSS.
 */

/** How long to keep looking for the target before giving up (element may be lazy). */
const LOOKUP_TIMEOUT_MS = 4000

/**
 * True when the user asked the OS to reduce motion. The CSS media query already
 * drops the glow keyframes, but the JS-driven `scrollIntoView` is a separate
 * animation, so gate its `behavior` on this too. Guarded because `matchMedia`
 * is absent in SSR/jsdom — treat "no matchMedia" as "no preference expressed".
 */
function prefersReducedMotion(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function findResourceTarget(id: string): HTMLElement | null {
  if (typeof document === "undefined") {
    return null
  }
  const byId = document.getElementById(id)
  if (byId) {
    return byId
  }
  const byData = document.querySelector<HTMLElement>(
    `[data-covi-highlight-id="${CSS.escape(id)}"]`,
  )
  return byData
}

function findTargets(id: string, fields: readonly string[]): HTMLElement[] {
  const resource = findResourceTarget(id)
  if (!resource || fields.length === 0) {
    return resource ? [resource] : []
  }

  const fieldTargets = fields
    .map((field) =>
      resource.querySelector<HTMLElement>(
        `[data-covi-highlight-field="${CSS.escape(field)}"]`,
      ),
    )
    .filter((element): element is HTMLElement => element !== null)

  return fieldTargets.length > 0 ? [...new Set(fieldTargets)] : [resource]
}

/**
 * A live glow: the rAF poll looking for the target, the pending auto-clear
 * timer, and the applied highlight. Tracked so a new trigger (or unmount) can
 * cancel an in-flight one instead of stacking timers/rAF loops.
 */
interface ActiveHighlight {
  cancel: () => void
}

export function useCoviHighlightTarget(): void {
  const router = useRouter()
  const isReady = router.isReady
  const targetId = readHighlightId(router.query?.[COVI_HIGHLIGHT_PARAM])
  // Latest router in a ref so the param-strip effect can read the live
  // pathname/query without listing `router` as a dep — that would re-run the
  // effect (and restart the glow) on every unrelated query churn.
  const routerRef = useRef(router)
  routerRef.current = router
  // The one in-flight glow (from either trigger). A fresh trigger cancels the
  // previous one so timers/rAF never stack.
  const activeRef = useRef<ActiveHighlight | null>(null)

  /**
   * Poll (via rAF) for the element with `id`, then glow it and auto-clear.
   * Shared by both triggers. `onArmed` runs once the glow is applied and
   * `onExpired` if the element never mounts within the budget — the URL path
   * uses these to strip its param either way; the event path passes neither.
   * Returns a cleanup that cancels the poll/timer and removes the glow.
   */
  const runHighlight = useCallback(
    (
      id: string,
      fields: readonly string[] = [],
      hooks?: { onArmed?: () => void; onExpired?: () => void },
    ) => {
      // A newer trigger supersedes any glow already in flight.
      activeRef.current?.cancel()

      let removeHighlight: (() => void) | null = null
      let clearTimer: number | undefined
      let rafId: number | undefined
      const startedAt = Date.now()
      let cancelled = false

      const cancel = () => {
        cancelled = true
        if (rafId !== undefined) {
          window.cancelAnimationFrame(rafId)
        }
        if (clearTimer !== undefined) {
          window.clearTimeout(clearTimer)
        }
        removeHighlight?.()
        removeHighlight = null
        if (activeRef.current === active) {
          activeRef.current = null
        }
      }

      const active: ActiveHighlight = { cancel }
      activeRef.current = active

      const startGlow = (elements: HTMLElement[]) => {
        elements[0]?.scrollIntoView({
          block: "nearest",
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        })
        const cleanups = elements.map(applyTransientHighlight)
        removeHighlight = () => cleanups.forEach((cleanup) => cleanup())
        clearTimer = window.setTimeout(() => {
          removeHighlight?.()
          removeHighlight = null
          if (activeRef.current === active) {
            activeRef.current = null
          }
        }, COVI_HIGHLIGHT_DURATION_MS)
        hooks?.onArmed?.()
      }

      const tick = () => {
        if (cancelled) {
          return
        }
        const elements = findTargets(id, fields)
        if (elements.length > 0) {
          startGlow(elements)
          return
        }
        if (Date.now() - startedAt >= LOOKUP_TIMEOUT_MS) {
          hooks?.onExpired?.()
          if (activeRef.current === active) {
            activeRef.current = null
          }
          return
        }
        rafId = window.requestAnimationFrame(tick)
      }

      tick()
      return cancel
    },
    [],
  )

  // Trigger 1 — URL param on arrival (cross-navigation create/edit). Keyed only
  // on the resolved id + readiness so unrelated query churn never restarts it.
  useEffect(() => {
    if (!isReady || !targetId) {
      return
    }
    const stripParam = () => {
      const current = routerRef.current
      const nextQuery = { ...current.query }
      delete nextQuery[COVI_HIGHLIGHT_PARAM]
      const navigation = buildWorkspaceAwareNavigation(
        current.asPath,
        current.pathname,
        nextQuery,
      )
      void current.replace(navigation.href, navigation.as, {
        shallow: true,
        scroll: false,
      })
    }
    // Strip the param whether or not the element ever shows, so a reload/back
    // never re-triggers it and it doesn't linger to re-fire on a later render.
    runHighlight(targetId, [], {
      onArmed: stripParam,
      onExpired: stripParam,
    })
    // Do not return this run's cancel function from the effect. `stripParam`
    // immediately changes `targetId`, so React runs the effect cleanup while
    // the glow is active; cancelling there would remove the class as soon as
    // it was applied. A new trigger cancels the prior run in `runHighlight`,
    // and the app-shell unmount effect below owns final cleanup.
  }, [isReady, targetId, runHighlight])

  // Trigger 2 — in-app signal (create/edit while already on the page). The
  // panel dispatches COVI_HIGHLIGHT_EVENT with the target id; glow it in place.
  useEffect(() => {
    const onHighlight = (event: Event) => {
      const detail = (event as CustomEvent<CoviHighlightDetail>).detail
      if (detail?.id) {
        runHighlight(detail.id, detail.fields ?? [])
      }
    }
    window.addEventListener(COVI_HIGHLIGHT_EVENT, onHighlight)
    return () => window.removeEventListener(COVI_HIGHLIGHT_EVENT, onHighlight)
  }, [runHighlight])

  // Cancel any in-flight glow on unmount.
  useEffect(() => {
    return () => {
      activeRef.current?.cancel()
    }
  }, [])
}
